import React from 'react';
import { useRouter } from 'next/router';

import { getLayout, IPrismicLayout } from 'providers/prismic/layout';
import { localeToLang } from 'providers/prismic/utils/localeToLang';
import { getProductBySlug, IPrismicProduct } from 'providers/prismic/product';
import { getProductIcelandBySlug, IPrismicProductIceland } from 'providers/prismic/productIceland';
import { getBasicProductBySlug, IPrismicBasicProduct } from 'providers/prismic/basicProduct';
import {
  getAlmajectProductBySlug,
  IPrismicAlmajectProduct,
} from 'providers/prismic/almajectProduct';
import { Layout } from 'containers/layout/Layout';
import { Slices } from 'containers/slices/Slices';
import { Error } from 'components/error/Error';
import { Header } from 'components/header/Header';
import { Product } from 'components/product/Product';
import { ProductIceland } from 'components/product-iceland/ProductIceland';
import { getVerification, IPrismicVerification } from 'providers/prismic/verification';
import { RichText } from 'components/rich-text/RichText';
import { Verification } from 'containers/verification/Verification';
import { createSlug } from 'providers/prismic/utils/createSlug';
import { config } from 'utils/config';
import { ProductAlmaject } from 'components/product-almaject/ProductAlmaject';

type Props = {
  preview: boolean;
  layout: IPrismicLayout;
  product?: IPrismicProduct;
  productIceland?: IPrismicProductIceland;
  basicProduct?: IPrismicBasicProduct;
  verification?: IPrismicVerification;
  almajectProduct?: IPrismicAlmajectProduct;
  uid?: string;
  url: string;
};

export default function ProductPage({
  preview,
  layout,
  product,
  productIceland,
  basicProduct,
  verification,
  almajectProduct,
  uid,
  url,
}: Props) {
  const router = useRouter();

  if (router.isFallback) {
    return <Loading />;
  }

  if (!product && !productIceland && !basicProduct && !almajectProduct) {
    return <Error title="Whoops!" statusCode={404} />;
  }

  if (productIceland && productIceland.uid === uid) {
    return (
      <Layout
        layout={layout}
        seo={{
          title: productIceland.title,
          image: productIceland.images[0]?.image || undefined,
          canonicalUrl: url,
        }}
        header={<Header fixed />}
        isPadded
        preview={preview}
      >
        <ProductIceland
          tagline={productIceland.tagline}
          title={productIceland.title}
          description={<RichText render={productIceland.description} />}
          drugType={productIceland.drugType}
          activeIngredient={productIceland.activeIngredient}
          form={productIceland.form}
          strengths={productIceland.strengths}
          sizes={productIceland.sizes}
          images={productIceland.images}
          warning={<RichText render={productIceland.warning} />}
          adNumber={productIceland.adNumber}
          links={productIceland.links}
        />
        <Slices slices={productIceland.slices} />
        {verification && (
          <Verification
            visible={true}
            title={verification.title}
            tagline={verification.tagline}
            text={<RichText render={verification.text} />}
            acceptLabel={verification.acceptLabel}
            declineLabel={verification.declineLabel}
            link={verification.link}
          />
        )}
      </Layout>
    );
  }

  if (basicProduct && basicProduct.uid === uid) {
    // const logoColor = basicProduct.hero?.textColor ? 'dark' : 'brand';
    // console.log(basicProduct.hero.background);

    return (
      <Layout
        layout={layout}
        seo={{
          title: basicProduct.title,
          image: basicProduct.images[0]?.image || undefined,
          canonicalUrl: url,
        }}
        header={<Header color="dark" fixed />}
        isPadded
        preview={preview}
      >
        <ProductIceland
          tagline={basicProduct.tagline}
          title={basicProduct.title}
          description={<RichText render={basicProduct.description} />}
          drugType={basicProduct.drugType}
          activeIngredient={basicProduct.activeIngredient}
          form={basicProduct.form}
          strengths={basicProduct.strengths}
          sizes={basicProduct.sizes}
          images={basicProduct.images}
          warning={<RichText render={basicProduct.warning} />}
          adNumber={basicProduct.adNumber}
          links={basicProduct.links}
          heroTitle={basicProduct.hero?.title}
          heroTagline={basicProduct.hero?.tagline}
          heroImage={basicProduct.hero?.image}
          heroTextColor={basicProduct.hero?.textColor}
          heroTextRight={basicProduct.hero?.textRight}
          heroVideo={basicProduct.hero?.video}
          heroBackground={basicProduct.hero?.background}
        />
        <Slices slices={basicProduct.slices} />
      </Layout>
    );
  }

  if (almajectProduct && almajectProduct.uid === uid) {
    // console.log('almajectProduct', almajectProduct);

    return (
      <Layout
        layout={layout}
        seo={{
          title: '', // almajectProduct.title as string
          image: almajectProduct.images[0]?.image || undefined,
          canonicalUrl: url,
        }}
        header={<Header color="dark" fixed />}
        isPadded
        preview={preview}
      >
        <ProductAlmaject
          tagline={almajectProduct.tagline}
          title={<RichText render={almajectProduct.title} />}
          presentation={<RichText render={almajectProduct.presentation} />}
          referenceDrug={<RichText render={almajectProduct.referenceDrug} />}
          therapeuticClass={<RichText render={almajectProduct.therapeuticClass} />}
          safetyDataSheet={almajectProduct.safetyDataSheet}
          barcodes={almajectProduct.barcodes}
          boxedWarning={almajectProduct.boxedWarning}
          sidenote={<RichText render={almajectProduct.sidenote} />}
          itemNumbers={almajectProduct.itemNumbers.map((itemNumber) => ({
            ...itemNumber,
            number: <RichText render={itemNumber.number} />,
          }))}
          images={almajectProduct.images}
          unitsOfSale={almajectProduct.unitsOfSale}
          variations={almajectProduct.variations}
          additionalInformation={almajectProduct.additionalInformation.map((info) => ({
            label: <RichText render={info.label} />,
            value: <RichText render={info.value} />,
          }))}
        />
      </Layout>
    );
  }

  return (
    <Layout
      layout={layout}
      seo={{
        title: product.seo?.title || product.title,
        description: product.seo?.description,
        image: product.seo?.image || product.image,
        canonicalUrl: url,
      }}
      header={<Header />}
      isPadded
      preview={preview}
    >
      <Product
        tagline={product.type}
        title={product.title || ''}
        image={product.image}
        brandImage={product.brandImage}
        link={product.link}
        rated={product.abRatedTo}
        ratedLabel={product.ratedLabel}
        warning={product.warning}
        hasRems={product.hasRems}
        remsLink={product.remsLink}
        pdfLink={product.pdfLink}
        pdfLabel={product.pdfLabel}
        externalAlert={process.env.THEME === 'almatica' ? false : true}
      >
        <Slices slices={product.slices} isProduct />
      </Product>
    </Layout>
  );
}

const Loading = () => <p>Loading...</p>;

export async function getStaticProps({ params, preview = false, previewData, locale }) {
  const lang = localeToLang(locale);
  const layout = await getLayout({ previewData, lang });

  if (process.env.PRISMIC_PRODUCT_TYPE === 'iceland') {
    const verification = await getVerification({ previewData, lang, redirects: layout?.redirects });
    const productIceland = await getProductIcelandBySlug(params.product, {
      previewData,
      lang,
      redirects: layout?.redirects,
    });
    const basicProduct = await getBasicProductBySlug(params.product, {
      previewData,
      lang,
      redirects: layout?.redirects,
    });

    return {
      props: {
        preview,
        layout,
        productIceland,
        basicProduct,
        verification,
        uid: params.product,
        url: createSlug({
          includeSiteUrl: true,
          lang: lang,
          prefix: 'product',
          uid: (productIceland || basicProduct)?.uid,
          layouts: [layout],
        }),
      },
      revalidate: 10,
    };
  }

  if (process.env.PRISMIC_PRODUCT_TYPE === 'almaject') {
    const almajectProduct = await getAlmajectProductBySlug(params.product, {
      previewData,
      lang,
      redirects: layout?.redirects,
    });

    return {
      props: {
        preview,
        layout,
        almajectProduct,
        uid: params.product,
        url: createSlug({
          includeSiteUrl: true,
          lang: lang,
          prefix: 'product',
          uid: almajectProduct?.uid,
          layouts: [layout],
        }),
      },
      revalidate: 10,
    };
  }

  const product = await getProductBySlug(params.product, {
    previewData,
    lang,
    redirects: layout?.redirects,
  });

  return {
    props: {
      preview,
      layout,
      product,
      url: createSlug({
        includeSiteUrl: true,
        lang: lang,
        prefix: 'product',
        uid: product?.uid,
        layouts: [layout],
      }),
    },
    revalidate: config.revalidate,
  };
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking',
  };
}
